import { DataTestIds } from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';

interface ContinueToStripeDialogProps {
  isOpen: boolean;
  /** An instalment plan still has payments to charge; a one-time purchase does not. */
  hasScheduledPayments: boolean;
  isPortalLoading: boolean;
  onConfirm(): void;
  onClose(): void;
}

/**
 * The warning before the page hands someone to Stripe.
 *
 * Worth a step of its own because leaving StartHub mid-task is the surprising
 * part: the member came to change a card and ends up on another company's
 * domain, and saying so first is what makes coming back feel like part of the
 * flow rather than a detour.
 */
export function ContinueToStripeDialog({
  isOpen,
  hasScheduledPayments,
  isPortalLoading,
  onConfirm,
  onClose,
}: ContinueToStripeDialogProps) {
  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={isPortalLoading ? () => undefined : onClose}
      title="Continue to Stripe?"
      size="small"
      className="BillingDialog"
      dataTestId={DataTestIds.BILLING.CONTINUE_TO_STRIPE_DIALOG}
      primaryAction={{
        label: 'Continue to Stripe',
        onClick: onConfirm,
        loading: isPortalLoading,
        dataTestId: DataTestIds.BILLING.CONTINUE_TO_STRIPE_CONFIRM,
      }}
      secondaryAction={isPortalLoading ? undefined : { label: 'Cancel', onClick: onClose }}
    >
      {/* The frame parts the two paragraphs with a blank line: 24px. */}
      <SDSFlexbox direction="column" gap="no-design-24" fullWidth>
        <SDSText
          type="body-large"
          text={
            hasScheduledPayments
              ? 'You’ll be redirected to Stripe, our secure payment provider, to update the payment method used for your remaining instalments.'
              : 'You’ll be redirected to Stripe, our secure payment provider, to update the payment method saved to your account.'
          }
        />
        <SDSText type="body-large" text="Return to StartHub when you have finished." />
      </SDSFlexbox>
    </SDSDialog>
  );
}
